import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { RESET_PASSWORD_LINK_MINUTES, passwordForgotFormSchema } from '@thrivehrm/shared';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { z } from 'zod';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthConfig, usePasswordForgot } from '@/features/auth/api';
import { AuthHeading, AuthLayout } from '@/features/auth/auth-layout';
import { Recaptcha, type RecaptchaHandle } from '@/features/auth/recaptcha';
import { ApiError } from '@/lib/api';

const searchSchema = z.object({
  /** Prefill from the login page's email field. */
  email: z.string().max(254).optional().catch(undefined),
});

export const Route = createFileRoute('/forgot-password')({
  validateSearch: searchSchema,
  beforeLoad: ({ context }) => {
    if (context.auth.user) throw redirect({ to: '/' });
  },
  component: ForgotPasswordPage,
});

const CAPTCHA_MESSAGES: Record<string, string> = {
  captcha_required: 'Tick "I\'m not a robot", then send the link again.',
  captcha_failed: 'The "I\'m not a robot" check didn\'t pass. Tick it again.',
};

/**
 * Forgot password (US-13, FR-AUTH-12, FR-AUTH-16, FR-AUTH-19, T-13A): email + the reCAPTCHA
 * checkbox (when configured) + "Send reset link". Whatever the email, the answer is the same
 * generic confirmation with "Back to sign in" — the page never says whether it is registered.
 */
function ForgotPasswordPage() {
  const { email: initialEmail } = Route.useSearch();
  const config = useAuthConfig();
  const forgot = usePasswordForgot();
  const [email, setEmail] = React.useState(initialEmail ?? '');
  const [fieldError, setFieldError] = React.useState<string | undefined>();
  const [error, setError] = React.useState<string | null>(null);
  const [token, setToken] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState<string | null>(null);
  const emailRef = React.useRef<HTMLInputElement>(null);
  const recaptcha = React.useRef<RecaptchaHandle>(null);
  const siteKey = config.data?.recaptchaSiteKey ?? null;

  const submit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (forgot.isPending || config.isPending) return;
    const parsed = passwordForgotFormSchema.safeParse({ email });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message);
      emailRef.current?.focus();
      return;
    }
    setFieldError(undefined);
    if (siteKey && !token) {
      setError(CAPTCHA_MESSAGES['captcha_required'] ?? null);
      return;
    }
    setError(null);
    forgot.mutate(
      { email: parsed.data.email, ...(token ? { captchaToken: token } : {}) },
      {
        onSuccess: (res) => {
          setSent(res.message);
        },
        onError: (err) => {
          recaptcha.current?.reset();
          const code = err instanceof ApiError ? err.problem.code : undefined;
          setError(
            (code ? CAPTCHA_MESSAGES[code] : undefined) ??
              (err instanceof ApiError ? err.message : 'Something went wrong. Try again.'),
          );
        },
      },
    );
  };

  if (sent !== null) {
    return (
      <AuthLayout titleId="forgot-title">
        <AuthHeading id="forgot-title" title="Check your email" />
        <Banner tone="success" live>
          {sent}
        </Banner>
        <p className="text-body text-muted-foreground">
          The link works once, for {RESET_PASSWORD_LINK_MINUTES} minutes. If nothing arrives, check
          your spam folder, or ask HR which email address they registered for you.
        </p>
        <Button asChild size="lg" variant="secondary" className="w-full">
          <Link to="/login">
            <ArrowLeft aria-hidden="true" />
            Back to sign in
          </Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout titleId="forgot-title">
      <AuthHeading id="forgot-title" title="Forgot your password?">
        Enter the email you sign in with. If it is registered, we&rsquo;ll email you a link to
        choose a new password.
      </AuthHeading>
      <form
        noValidate
        aria-label="Request a reset link"
        className="flex flex-col gap-4"
        onSubmit={submit}
      >
        {error ? (
          <Banner tone="danger" title="The link wasn't sent" live>
            {error}
          </Banner>
        ) : null}
        <FormField label="Email" error={fieldError}>
          <Input
            ref={emailRef}
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            spellCheck={false}
            autoCapitalize="off"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        </FormField>
        {config.isPending ? (
          <Skeleton className="h-[78px] w-[304px] max-w-full rounded-md" />
        ) : siteKey ? (
          <Recaptcha ref={recaptcha} siteKey={siteKey} onToken={setToken} />
        ) : null}
        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={forgot.isPending}
          disabled={config.isPending}
        >
          Send reset link
        </Button>
        <Link
          to="/login"
          className="self-start rounded-sm text-body text-primary underline-offset-4 hover:underline"
        >
          Back to sign in
        </Link>
      </form>
    </AuthLayout>
  );
}
